import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, FolderOpen, Image as ImageIcon, Loader2, Video } from 'lucide-react';
import { FileCategoryChip } from '@/components/documents/FileCategoryChip';
import { PatientFileBrowseControls } from '@/components/documents/PatientFileBrowseControls';
import { Button } from '@/components/ui/Button';
import { usePatientDocuments, type DocumentRow } from '@/hooks/useDocuments';
import { usePatientFileCategories } from '@/hooks/usePatientFileCategories';
import { documentListMeta } from '@/lib/documentSource';
import {
  ALL_FILE_CATEGORIES_ID,
  fileCategoryFilterCounts,
  resolveFileCategory,
} from '@/lib/patientFileCategories';
import { filterPatientFilesForBrowse } from '@/lib/patientFileSearch';
import { readPatientFileSort, sortPatientFiles } from '@/lib/patientFileSort';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';

const ICON = 1.75;

function fileIcon(mimeType: string) {
  if (mimeType.startsWith('video/')) return Video;
  if (mimeType.startsWith('image/')) return ImageIcon;
  return FileText;
}

/**
 * Patient files inside the open file view's left tool. Picking a row swaps
 * the file without closing the preview. Search and fil-tags match journal writing.
 */
export function FilePreviewBrowser({
  clinicId,
  patientId,
  userId,
  activeDocumentId,
  resolving,
  onOpen,
}: {
  clinicId: string | null;
  patientId: string | null;
  userId?: string | null;
  activeDocumentId: string;
  /** True while the open file's patient is still loading. */
  resolving?: boolean;
  onOpen: (doc: DocumentRow) => void;
}) {
  const { t } = useTranslation();
  const docs = usePatientDocuments(clinicId, patientId);
  const categoriesQuery = usePatientFileCategories(clinicId);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState(ALL_FILE_CATEGORIES_ID);
  const [sort] = useState(() => readPatientFileSort(userId));
  const listRef = useRef<HTMLUListElement>(null);
  const scopedPatient = useRef<string | null>(null);

  useEffect(() => {
    if (!patientId) return;
    if (scopedPatient.current && scopedPatient.current !== patientId) {
      setQuery('');
      setFilter(ALL_FILE_CATEGORIES_ID);
    }
    scopedPatient.current = patientId;
  }, [patientId]);

  const categories = useMemo(() => categoriesQuery.data ?? [], [categoriesQuery.data]);
  const rows = useMemo(() => docs.data ?? [], [docs.data]);
  const visible = useMemo(
    () =>
      sortPatientFiles(
        filterPatientFilesForBrowse(rows, {
          query,
          categoryId: filter,
          extraText: (doc) => [
            resolveFileCategory(categories, doc.category_id)?.name ??
              t('patients.files.uncategorized'),
            documentListMeta({
              mimeType: doc.mime_type,
              sizeBytes: doc.size_bytes,
              createdAt: doc.created_at,
              documentDate: doc.document_date,
              tags: doc.tags,
              t,
            }),
          ],
        }),
        sort,
      ),
    [categories, filter, query, rows, sort, t],
  );
  const counts = fileCategoryFilterCounts(rows, categories);

  useEffect(() => {
    const node = listRef.current?.querySelector<HTMLElement>('[aria-current="true"]');
    if (typeof node?.scrollIntoView === 'function') {
      node.scrollIntoView({ block: 'nearest' });
    }
  }, [activeDocumentId, visible.length]);

  const openAt = (index: number) => {
    const doc = visible[index];
    if (!doc || doc.id === activeDocumentId) return;
    onOpen(doc);
    requestAnimationFrame(() => {
      listRef.current?.querySelector<HTMLButtonElement>(`[data-file-id="${doc.id}"]`)?.focus();
    });
  };

  const onListKeyDown = (event: KeyboardEvent) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    event.preventDefault();
    const index = visible.findIndex((doc) => doc.id === activeDocumentId);
    const delta = event.key === 'ArrowDown' ? 1 : -1;
    const start = index < 0 ? (delta > 0 ? -1 : 0) : index;
    openAt(start + delta);
  };

  if (!patientId) {
    return (
      <div className="fp-files">
        <div className="fp-files-empty">
          {resolving ? (
            <p role="status">{t('app.loading')}</p>
          ) : (
            <p>{t('documents.browser.no_patient')}</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="fp-files">
      {docs.isLoading ? (
        <div className="fp-files-empty" role="status">
          <Loader2 className="h-5 w-5 animate-spin" strokeWidth={ICON} aria-hidden />
          {t('app.loading')}
        </div>
      ) : docs.isError ? (
        <div className="fp-files-empty" role="alert">
          <p>{t('documents.load_error')}</p>
          <Button type="button" variant="secondary" onClick={() => void docs.refetch()}>
            {t('app.retry')}
          </Button>
        </div>
      ) : rows.length === 0 ? (
        <div className="fp-files-empty">
          <FolderOpen className="h-6 w-6" strokeWidth={ICON} aria-hidden />
          <p>{t('patients.files.empty')}</p>
        </div>
      ) : (
        <>
          <PatientFileBrowseControls
            query={query}
            onQuery={setQuery}
            filter={filter}
            onFilter={setFilter}
            categories={categories}
            counts={counts}
            searchTestId="file-browser-search"
          />
          {visible.length === 0 ? (
            <div className="fp-files-empty">
              <p>{t('patients.files.none')}</p>
            </div>
          ) : (
            <ul
              ref={listRef}
              className="fp-files-list"
              aria-label={t('documents.browser.list')}
              onKeyDown={onListKeyDown}
            >
              {visible.map((doc, index) => {
                const Icon = fileIcon(doc.mime_type);
                const category = resolveFileCategory(categories, doc.category_id);
                const current = doc.id === activeDocumentId;
                return (
                  <li key={doc.id}>
                    <button
                      type="button"
                      className={cn('fp-file-hit', current && 'is-on')}
                      data-testid={`file-browser-file-${doc.id}`}
                      data-file-id={doc.id}
                      aria-current={current ? 'true' : undefined}
                      onClick={() => openAt(index)}
                    >
                      <span className="fp-file-name">
                        <Icon className="fp-file-icon" aria-hidden />
                        {doc.original_filename}
                      </span>
                      <span className="pf-file-meta">
                        <span className={cn(TYPE.small, 'pf-file-meta-text text-ds-muted')}>
                          {documentListMeta({
                            mimeType: doc.mime_type,
                            sizeBytes: doc.size_bytes,
                            createdAt: doc.created_at,
                            documentDate: doc.document_date,
                            tags: doc.tags,
                            t,
                          })}
                        </span>
                        <FileCategoryChip
                          className="pf-file-tag"
                          name={category?.name ?? t('patients.files.uncategorized')}
                          colorToken={category?.color_token ?? 'muted'}
                        />
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
